import { getAuthHeaders } from "./authHelper";

export type HotelRoomTypeDto = {
    hotelRoomTypeID: number;
    hotelID: number;
    name: string;
    capacity: number;
    pricePerNight: number;
    availableRooms: number;
    isActive: boolean;
};

export type HotelDto = {
    hotelID: number;
    eventID: number;
    name: string;
    address?: string | null;
    description?: string | null;
    bookingDeadline?: string | null;
    roomingDeadline?: string | null;
    paymentDeadline?: string | null;
    startBooking?: string | null;
    endBooking?: string | null;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string | null;
    roomTypes: HotelRoomTypeDto[];
};

export type HotelUpsertDto = {
    eventID: number;
    name: string;
    address?: string | null;
    description?: string | null;
    bookingDeadline?: string | null;
    paymentDeadlineDays?: number | null;
    roomingDeadline?: string | null;
    isActive: boolean;
};

export type HotelRoomTypeUpsertDto = {
    name: string;
    capacity: number;
    pricePerNight: number;
    availableRooms: number;
    isActive: boolean;
};

export type HotelBookingGuestDto = {
    hotelBookingGuestID?: number;
    personName?: string | null;
    role?: string | null;
    email?: string | null;
    phone?: string | null;
};

export type HotelBookingRoomDto = {
    hotelBookingRoomID: number;
    hotelRoomTypeID: number;
    roomTypeName?: string | null;
    checkInDate: string;
    checkOutDate: string;
    guestsCount: number;
    pricePerNight: number;
    totalPrice: number;
    guests: HotelBookingGuestDto[];
};

export type HotelBookingDto = {
    hotelBookingID: number;
    paymentReference?: string | null;

    eventID: number;
    hotelID: number;
    hotelName?: string | null;

    country: string;
    contactName?: string | null;
    contactEmail?: string | null;

    whatsappCountryCode?: string | null;
    whatsappNumber?: string | null;
    comment?: string | null;

    totalPrice: number;
    status: string;

    submittedAt?: string | null;
    approvedAt?: string | null;
    paidAt?: string | null;
    createdAt: string;

    roomingDeadline?: string | null;
    bookingDeadline?: string | null;

    paymentDeadline?: string | null;
    paymentDeadlineDays?: number | null;

    paymentLink?: string | null;
    isPaid?: boolean | null;

    rooms: HotelBookingRoomDto[];
};

export type HotelBookingCreateRoomDto = {
    hotelRoomTypeID: number;
    checkInDate: string;
    checkOutDate: string;
    guestsCount: number;
};

export type HotelBookingCreateDto = {
    eventID: number;
    hotelID: number;
    userID: number;

    country: string;

    contactName?: string | null;
    contactEmail?: string | null;

    whatsappCountryCode?: string | null;
    whatsappNumber?: string | null;

    comment?: string | null;

    rooms: HotelBookingCreateRoomDto[];
};

export type HotelBookingUpdateDto = {
    country: string;

    contactName?: string | null;
    contactEmail?: string | null;

    whatsappCountryCode?: string | null;
    whatsappNumber?: string | null;

    comment?: string | null;

    rooms: {
        hotelRoomTypeID: number;
        checkInDate: string;
        checkOutDate: string;
        guestsCount: number;
    }[];
};

async function request<T>(
    apiUrl: string,
    path: string,
    options: RequestInit = {}
): Promise<T> {
    const res = await fetch(`${apiUrl}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
            ...(options.headers || {}),
        },
    });

    if (!res.ok) {
        let message = "Request failed";

        try {
            const data = await res.json();
            message = data?.message || data?.title || message;
        } catch {
            message = await res.text();
        }

        throw new Error(message);
    }

    if (res.status === 204) {
        return undefined as T;
    }

    return res.json();
}


// ------------------------------------------------------
// Admin hotels
// ------------------------------------------------------

export async function getHotelsForEvent(
    apiUrl: string,
    eventId: number
): Promise<HotelDto[]> {
    return request<HotelDto[]>(
        apiUrl,
        `/api/hotel-admin/events/${eventId}/hotels`,
        {
            headers: getAuthHeaders(),
        }
    );
}

export async function getHotel(
    apiUrl: string,
    hotelId: number
): Promise<HotelDto> {
    return request<HotelDto>(
        apiUrl,
        `/api/hotel-admin/hotels/${hotelId}`
    );
}

export async function createHotel(
    apiUrl: string,
    dto: HotelUpsertDto
): Promise<{ hotelID: number }> {
    return request<{ hotelID: number }>(
        apiUrl,
        `/api/hotel-admin/hotels`,
        {
            method: "POST",
            body: JSON.stringify(dto),
        }
    );
}

export async function updateHotel(
    apiUrl: string,
    hotelId: number,
    dto: HotelUpsertDto
): Promise<{ message: string }> {
    return request<{ message: string }>(
        apiUrl,
        `/api/hotel-admin/hotels/${hotelId}`,
        {
            method: "PUT",
            body: JSON.stringify(dto),
        }
    );
}

export async function deleteHotel(
    apiUrl: string,
    hotelId: number
): Promise<{ message: string }> {
    return request<{ message: string }>(
        apiUrl,
        `/api/hotel-admin/hotels/${hotelId}`,
        {
            method: "DELETE",
        }
    );
}

export async function createRoomType(
    apiUrl: string,
    hotelId: number,
    dto: HotelRoomTypeUpsertDto
): Promise<{ hotelRoomTypeID: number }> {
    return request<{ hotelRoomTypeID: number }>(
        apiUrl,
        `/api/hotel-admin/hotels/${hotelId}/room-types`,
        {
            method: "POST",
            body: JSON.stringify(dto),
        }
    );
}

export async function updateRoomType(
    apiUrl: string,
    roomTypeId: number,
    dto: HotelRoomTypeUpsertDto
): Promise<{ message: string }> {
    return request<{ message: string }>(
        apiUrl,
        `/api/hotel-admin/room-types/${roomTypeId}`,
        {
            method: "PUT",
            body: JSON.stringify(dto),
        }
    );
}

export async function deleteRoomType(
    apiUrl: string,
    roomTypeId: number
): Promise<{ message: string }> {
    return request<{ message: string }>(
        apiUrl,
        `/api/hotel-admin/room-types/${roomTypeId}`,
        {
            method: "DELETE",
        }
    );
}


// ------------------------------------------------------
// Hotel bookings
// ------------------------------------------------------

export async function getHotelBookingsForEvent(
    apiUrl: string,
    eventId: number
): Promise<HotelBookingDto[]> {
    return request<HotelBookingDto[]>(
        apiUrl,
        `/api/hotel-bookings/event/${eventId}`
    );
}

export async function getHotelBooking(
    apiUrl: string,
    bookingId: number
): Promise<HotelBookingDto> {
    return request<HotelBookingDto>(
        apiUrl,
        `/api/hotel-bookings/${bookingId}`
    );
}

export async function getHotelBookingsForUserEvent(
    apiUrl: string,
    userId: number,
    eventId: number
): Promise<HotelBookingDto[]> {
    return request<HotelBookingDto[]>(
        apiUrl,
        `/api/hotel-bookings/user/${userId}/event/${eventId}`
    );
}


// ------------------------------------------------------
// Create booking
// ------------------------------------------------------

export async function createHotelBooking(
    apiUrl: string,
    dto: HotelBookingCreateDto
): Promise<{
    hotelBookingID: number;
    totalPrice: number;
    status: string;
}> {
    return request(
        apiUrl,
        `/api/hotel-bookings`,
        {
            method: "POST",
            body: JSON.stringify(dto),
        }
    );
}


// ------------------------------------------------------
// Update booking BEFORE PAYMENT
// ------------------------------------------------------

export async function updateHotelBooking(
    apiUrl: string,
    bookingId: number,
    dto: HotelBookingUpdateDto
): Promise<{
    hotelBookingID: number;
    totalPrice: number;
    status: string;
    paymentLinkReset: boolean;
}> {
    return request(
        apiUrl,
        `/api/hotel-bookings/${bookingId}`,
        {
            method: "PUT",
            body: JSON.stringify(dto),
        }
    );
}


// ------------------------------------------------------
// Approve / reject / payment
// ------------------------------------------------------

export async function approveHotelBooking(
    apiUrl: string,
    bookingId: number
): Promise<{
    message: string;
    status: string;
    paymentLinkCreated?: boolean;
    paymentLink?: string | null;
    paymentReference?: string | null;
    paymentDeadline?: string | null;
    paymentLinkError?: string | null;
}> {
    return request(
        apiUrl,
        `/api/hotel-admin/bookings/${bookingId}/approve`,
        {
            method: "POST",
        }
    );
}

export async function rejectHotelBooking(
    apiUrl: string,
    bookingId: number
): Promise<{
    message: string;
    status: string;
}> {
    return request(
        apiUrl,
        `/api/hotel-admin/bookings/${bookingId}/reject`,
        {
            method: "POST",
        }
    );
}

export async function markHotelBookingPaid(
    apiUrl: string,
    bookingId: number
): Promise<{
    message: string;
    status: string;
    paidAt?: string | null;
}> {
    return request(
        apiUrl,
        `/api/hotel-admin/bookings/${bookingId}/mark-paid`,
        {
            method: "POST",
        }
    );
}


// ------------------------------------------------------
// Guests / rooming list
// ------------------------------------------------------

export type UpdateGuestsDto = {
    rooms: {
        hotelBookingRoomID: number;
        guests: {
            personName?: string | null;
            role?: string | null;
            email?: string | null;
            phone?: string | null;
        }[];
    }[];
};

export async function updateHotelBookingGuests(
    apiUrl: string,
    bookingId: number,
    dto: UpdateGuestsDto
): Promise<{ message: string }> {
    return request<{ message: string }>(
        apiUrl,
        `/api/hotel-bookings/${bookingId}/guests`,
        {
            method: "PUT",
            body: JSON.stringify(dto),
        }
    );
}

export async function completeHotelBooking(
    apiUrl: string,
    bookingId: number
): Promise<{ message: string; status: string }> {
    return request<{ message: string; status: string }>(
        apiUrl,
        `/api/hotel-bookings/${bookingId}/complete`,
        {
            method: "POST",
        }
    );
}


// ------------------------------------------------------
// Admin booking status
// ------------------------------------------------------

export type UpdateHotelBookingStatusRequest = {
    status: string;
    note?: string;
};

export async function updateHotelBookingStatus(
    apiUrl: string,
    hotelBookingID: number,
    payload: UpdateHotelBookingStatusRequest
) {
    const response = await fetch(
        `${apiUrl}/api/hotel-booking/admin/${hotelBookingID}/status`,
        {
            method: "PUT",
            headers: {
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        }
    );

    if (!response.ok) {
        const text = await response.text();

        throw new Error(
            text || "Could not update hotel booking status"
        );
    }

    if (response.status === 204) {
        return null;
    }

    return response.json();
}


// ------------------------------------------------------
// Payment link
// ------------------------------------------------------

export async function createHotelPaymentLink(
    apiUrl: string,
    hotelBookingID: number
) {
    return request<{
        hotelBookingID: number;
        paymentLink: string;
        paymentReference?: string | null;
        paymentDeadline?: string | null;
        existing?: boolean;
    }>(
        apiUrl,
        `/api/hotel-admin/bookings/${hotelBookingID}/payment-link`,
        {
            method: "POST",
        }
    );
}

// ------------------------------------------------------
// Admin update booking BEFORE PAYMENT
// ------------------------------------------------------

export async function updateHotelBookingAdmin(
    apiUrl: string,
    bookingId: number,
    dto: HotelBookingUpdateDto
): Promise<{
    hotelBookingID: number;
    totalPrice: number;
    status: string;
    paymentLinkReset: boolean;
}> {
    return request(
        apiUrl,
        `/api/hotel-admin/bookings/${bookingId}`,
        {
            method: "PUT",
            body: JSON.stringify(dto),
        }
    );
}